import CustomerProjectSelection from "../../CustomerProjectSelection";
import _ from 'underscore'
import CustomerStore from '../../store/CustomerStore'
import TaskManagementStore from '../TaskManagementStore'
import ProjectStore from '../../store/ProjectStore'
import StringIndex from '../../util/StringIndex'
import {arrayToSet} from '../../atUtils'
import {getLocalStorage} from '../../util/LocalStorage'

export const createCPSelectionFromSingleCPSelectionObject = (singleCPSelection) => {
    let qualifier = CustomerProjectSelection.Qualifier.ALL;
    const customers = [];
    const projects = [];

    if (singleCPSelection.customerId) {
        qualifier = CustomerProjectSelection.Qualifier.SELECTED;
        if (singleCPSelection.projectId)
            projects.push(singleCPSelection.projectId);
        else
            customers.push(singleCPSelection.customerId);
    }

    return new CustomerProjectSelection(qualifier, customers, projects);
}

export const createIntersectedCPSelection = (filterCPSelection, singleCPSelection, customersMap, projectsMap) => {
    const convertedSingleCPSelection = createCPSelectionFromSingleCPSelectionObject(singleCPSelection)
    const convertedFilterCPSelection = CustomerProjectSelection.fromPlainObject(filterCPSelection)
    return createIntersectedCPSelectionInternal(convertedSingleCPSelection, convertedFilterCPSelection, customersMap, projectsMap)
}

const createIntersectedCPSelectionInternal = (selection, otherSelection, customersMap, projectsMap) => {
    let intersectionQualifier;
    if (selection.qualifier === CustomerProjectSelection.Qualifier.ALL) {
        intersectionQualifier = otherSelection.qualifier;
    }
    else if (selection.qualifier === CustomerProjectSelection.Qualifier.ALL_ACTIVE) {
        if (otherSelection.qualifier === CustomerProjectSelection.Qualifier.ALL)
            intersectionQualifier = selection.qualifier;
        else
            intersectionQualifier = otherSelection.qualifier;
    }
    else if (selection.qualifier === CustomerProjectSelection.Qualifier.SELECTED) {
        intersectionQualifier = selection.qualifier;
    }

    const intersectionCustomersSet = {};
    selection.customers.forEach(customerId => {
        const customer = customersMap[customerId];
        if (customer && otherSelection.includesProject(customer))
            intersectionCustomersSet[customer.id] = true;
    });
    otherSelection.customers.forEach(customerId => {
        const customer = customersMap[customerId];
        if (customer && selection.includesCustomer(customer))
            intersectionCustomersSet[customer.id] = true;
    });
    const intersectionCustomers = _.keys(intersectionCustomersSet);

    const intersectionProjectsSet = {};
    selection.projects.forEach(projectId => {
        const project = projectsMap[projectId]
        if (project && otherSelection.includesProject(project))
            intersectionProjectsSet[projectId] = true;
    });
    otherSelection.projects.forEach(projectId => {
        const project = projectsMap[projectId]
        if (project && selection.includesProject(project))
            intersectionProjectsSet[projectId] = true;
    });
    const intersectionProjects = _.keys(intersectionProjectsSet);

    const intersectionActiveOnly = selection.activeOnly || otherSelection.activeOnly;

    return new CustomerProjectSelection(intersectionQualifier, intersectionCustomers, intersectionProjects, intersectionActiveOnly);
}

export function isCustomerAssignedForManage(customersMA, id, withIncludeSelf = true) {
    const assignment = customersMA[id]
    return assignment !== undefined && (!withIncludeSelf || assignment.includeSelf === true)
}

export function isProjectAssignedForManage(projectsMA, id, withIncludeSelf = true) {
    const assignment = projectsMA[id]
    return assignment !== undefined && (!withIncludeSelf || assignment.includeSelf === true)
}

export function isTaskAssignedForManage(tasksMA, id, withIncludeSelf = true) {
    const assignment = tasksMA[id]
    return assignment !== undefined && (!withIncludeSelf || assignment.includeSelf === true)
}

export function isPossibleToAddProjectsToCustomer(id, viewerSettings, customersMA) {
    return viewerSettings.hasEditHierarchyRight &&
           (viewerSettings.hasAccessToAllManagementScope || isCustomerAssignedForManage(customersMA, id, false))
}

export function isPossibleToManageProject(project, viewerSettings, customersMa, projectsMA) {
    return viewerSettings.hasEditHierarchyRight &&
           (viewerSettings.hasAccessToAllManagementScope
            || isCustomerAssignedForManage(customersMa, project.customerId, false)
            || isProjectAssignedForManage(projectsMA, project.id)
           );
}

export function getCustomerIdsFilteredByAssignments(customerIds, customerMA, withIncludeSelf = true) {
    return customerIds.filter(cId => isCustomerAssignedForManage(customerMA, cId, withIncludeSelf))
}

export function getProjectIdsFilteredByAssignments(projects, ma, withIncludeSelf = true) {
    return projects.filter(project =>
        isCustomerAssignedForManage(ma.customers, project.customerId, false)
        || isProjectAssignedForManage(ma.projects, project.id, withIncludeSelf)
    ).map(p => p.id)
}

export function getProjectsFilteredByAssignments(projects, ma, withIncludeSelf = true) {
    return projects.filter(project =>
        isCustomerAssignedForManage(ma.customers, project.customerId, false)
        || isProjectAssignedForManage(ma.projects, project.id, withIncludeSelf)
    )
}

export function getTaskIdsFilteredByAssignments(tasks, ma) {
    return tasks.filter(task =>
        isCustomerAssignedForManage(ma.customers, task.customerId, false)
        || isProjectAssignedForManage(ma.projects, task.projectId, false)
        || isTaskAssignedForManage(ma.tasks, task.id)
    ).map(t => t.id)
}

export const filterEntitiesBySearch = (entities, search) => {
    if (search) {
        const stringIndex = new StringIndex()
        entities.forEach(entity => {
            stringIndex.add(entity.name, entity.id)
        })
        const tokens = stringIndex.getTokenizer().getTokens(search.toLowerCase())
        const searchIds = arrayToSet(Object.keys(stringIndex.search(tokens)))
        return entities.filter(entity => searchIds[entity.id] === true)
    }
    return entities
}

export const removeCustomerFromCPSelection = (cpSelection, customerId, projectsMap) => {
    const filteredCustomers = cpSelection.customers.filter(cId => cId !== customerId)
    const filteredProjects = cpSelection.projects.filter(pId => projectsMap[pId].customerId !== customerId)
    return {
        ...cpSelection,
        qualifier: filteredProjects.length !== 0 || cpSelection.customers.length !== 0 ? CustomerProjectSelection.Qualifier.SELECTED : cpSelection.qualifier,
        customers: filteredCustomers,
        projects: filteredProjects
    }
}

export const removeProjectFromCPSelection = (projectId, cpSelection) => {
    const filteredProjects = cpSelection.projects.filter(pId => pId !== projectId)
    return {
        ...cpSelection,
        qualifier: filteredProjects.length !== 0 || cpSelection.customers.length !== 0 ? CustomerProjectSelection.Qualifier.SELECTED : cpSelection.qualifier,
        projects: filteredProjects
    }
}
export const setCpPanelClosedStateInLocalStorage = (closed) => {
    getLocalStorage().setItem('customersProjectsPanelClosed', closed)
}

export const getCpPanelClosedStateInLocalStorage = () => {
    return !!getLocalStorage().getItem('customersProjectsPanelClosed')
}

const SINGLE_CP_SELECTION_KEY = 'singleCPSelection'

export const saveSingleCPSelectionToLocalStorage = (selection) => {
    getLocalStorage().setItem(SINGLE_CP_SELECTION_KEY, selection)
}

export const getSingleCPSelectionFromLocalStorage = () => {
    return getLocalStorage().getItem(SINGLE_CP_SELECTION_KEY)
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/common/utils.js